/**
 * Filtro "chi ha creato" per le Task commerciali (sales_activities).
 *
 * Semantica colonne (verificata su 03_crm_clients.sql e sui punti di insert:
 * useActivitySave, QuickTaskModal, TaskCreationModal):
 * - user_id     = chi ha creato la task (NOT NULL)
 * - assigned_to = chi la deve fare (nullable)
 *
 * Logica pura, senza dipendenze da React o Supabase, testata in
 * __tests__/salesActivityCreatorFilter.test.ts
 */

export type CreatorFilter = 'assigned_by_me' | 'created_by_me' | 'assigned_to_me';

export const CREATOR_FILTER_OPTIONS: Array<{ value: CreatorFilter; label: string; color: string }> = [
  { value: 'assigned_by_me', label: 'Assegnate da me', color: 'purple' },
  { value: 'created_by_me', label: 'Create da me', color: 'blue' },
  { value: 'assigned_to_me', label: 'Assegnate a me da altri', color: 'green' },
];

export interface ActivityScope {
  /** Filtro server su assigned_to (null = nessun filtro) */
  assignedTo: string | null;
  /** Filtro server su user_id (null = nessun filtro) */
  createdBy: string | null;
}

/**
 * Traduce filtro creatore + filtro utente (assegnatario) nei vincoli della query.
 *
 * Senza filtro creatore il comportamento resta quello di prima (assigned_to = utente scelto).
 * Con filtro creatore l'utente scelto nel menu "Utente" restringe l'assegnatario solo se è
 * una persona diversa da me: "assegnate da me a me stesso" non avrebbe senso, e il menu
 * parte sempre da me, quindi senza questa regola il filtro non mostrerebbe mai niente.
 *
 * "Assegnate a me da altri" riguarda sempre me come assegnatario, qualunque utente sia
 * scelto nel menu: il creatore diverso da me lo controlla matchesCreatorFilter.
 */
export function resolveActivityScope(
  creatorFilter: CreatorFilter | null | undefined,
  selectedUserId: string | null | undefined,
  currentUserId: string | null | undefined,
): ActivityScope {
  const selected = selectedUserId ?? null;
  if (!creatorFilter || !currentUserId) {
    return { assignedTo: selected, createdBy: null };
  }
  if (creatorFilter === 'assigned_to_me') {
    return { assignedTo: currentUserId, createdBy: null };
  }
  return {
    assignedTo: selected && selected !== currentUserId ? selected : null,
    createdBy: currentUserId,
  };
}

/**
 * Condizione PostgREST della vista normale di un utente: le task assegnate a lui piu'
 * quelle che ha creato senza assegnatario (nessun altro le vedrebbe come sue).
 * L'id viene ripulito: solo caratteri di un uuid.
 */
export function assigneeScopeCondition(userId: string): string {
  const id = userId.replace(/[^0-9a-fA-F-]/g, '');
  return `assigned_to.eq.${id},and(assigned_to.is.null,user_id.eq.${id})`;
}

interface ActivityPeople {
  user_id?: string | null;
  assigned_to?: string | null;
}

/**
 * Controllo lato client sulle righe già caricate (la query filtra già user_id o assigned_to).
 * "Assegnate da me" = create da me e date a qualcun altro (assegnatario presente e diverso).
 * "Assegnate a me da altri" = assegnate a me e create da un'altra persona.
 */
export function matchesCreatorFilter(
  activity: ActivityPeople,
  creatorFilter: CreatorFilter | null | undefined,
  currentUserId: string | null | undefined,
): boolean {
  if (!creatorFilter || !currentUserId) return true;
  if (creatorFilter === 'assigned_to_me') {
    return activity.assigned_to === currentUserId && !!activity.user_id && activity.user_id !== currentUserId;
  }
  if (activity.user_id !== currentUserId) return false;
  if (creatorFilter === 'assigned_by_me') {
    return !!activity.assigned_to && activity.assigned_to !== currentUserId;
  }
  return true;
}

interface ActivityWithCreator extends ActivityPeople {
  creator?: { full_name?: string | null; email?: string | null } | null;
}

/**
 * Nome da mostrare in "Assegnata da <nome>": solo se creatore e assegnatario sono
 * persone diverse e il creatore ha un nome leggibile. Altrimenti null.
 */
export function getAssignedByName(activity: ActivityWithCreator): string | null {
  if (!activity.user_id || !activity.assigned_to) return null;
  if (activity.user_id === activity.assigned_to) return null;
  const name = activity.creator?.full_name?.trim() || activity.creator?.email?.trim();
  return name || null;
}
